/**
 * "Export" — the controls/tests surfaces as an Excel workbook grouped by
 * requirement citation, for an auditor reading the engagement offline.
 *
 * Two entry points because the two surfaces are scoped differently (an audit
 * cycle vs. the workspace), but both hit the same server-side workbook writer
 * (`requirement-tests-export.service`), so the two exports cannot drift.
 */

import { useMutation } from '@tanstack/react-query'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@deepguide-ai/ui/button'
import { trpc } from '@/lib/trpc'
import { downloadBase64File } from '@/utils/download'
import { cn } from '@/lib/utils'

interface ExportResult {
  filename: string
  contentType: string
  data: string
}

function ExportButton({
  onExport,
  isPending,
  className,
}: {
  onExport: () => void
  isPending: boolean
  className?: string
}) {
  return (
    <Button
      variant='outline'
      size='sm'
      onClick={onExport}
      disabled={isPending}
      title='Download controls and their tests as an Excel workbook, grouped by requirement'
      className={cn('h-8 shrink-0 gap-1.5 rounded-sm text-[12px]', className)}
    >
      {isPending ? (
        <Loader2 className='h-3.5 w-3.5 animate-spin' />
      ) : (
        <Download className='h-3.5 w-3.5' />
      )}
      {isPending ? 'Exporting…' : 'Export'}
    </Button>
  )
}

function downloadExport(data: ExportResult, what: string) {
  downloadBase64File(data.data, data.filename, data.contentType)
  toast.success(`${what} exported`)
}

/** Auditor portal — the cycle's Controls page. */
export function AuditorControlsExportButton({
  auditCycleId,
  className,
}: {
  auditCycleId: string
  className?: string
}) {
  const exportMutation = useMutation({
    ...trpc.auditor.exportControlsWorkbook.mutationOptions(),
    onSuccess: (data) => downloadExport(data, 'Controls'),
    onError: () => toast.error('Failed to export controls'),
  })

  return (
    <ExportButton
      onExport={() => exportMutation.mutate({ auditCycleId })}
      isPending={exportMutation.isPending}
      className={className}
    />
  )
}

/**
 * Customer portal — the workspace's Tests page.
 *
 * Exports what the page is showing, not the whole workspace: it forwards the
 * list's own query input plus the three view filters the list applies
 * client-side. The workbook's Export Info sheet restates them, so a filtered
 * file can't be mistaken for the complete list.
 */
export function WorkspaceTestsExportButton({
  frameworkId,
  filters,
  inclusion,
  kind,
  hideNa,
  className,
}: {
  /** `ComplianceFrameworkDef.id` from the page's framework picker, if pinned. */
  frameworkId?: string
  /** The object the page already built for `listAllTests`. */
  filters?: Record<string, unknown>
  inclusion?: 'core' | 'scope' | 'extended'
  kind?: 'policy' | 'automated' | 'manual'
  hideNa?: boolean
  className?: string
}) {
  const exportMutation = useMutation({
    ...trpc.guidedTemplate.exportTestsByRequirement.mutationOptions(),
    onSuccess: (data) => downloadExport(data, 'Tests'),
    onError: (error: Error) =>
      toast.error(error.message || 'Failed to export tests'),
  })

  return (
    <ExportButton
      onExport={() =>
        exportMutation.mutate({ frameworkId, filters, inclusion, kind, hideNa })
      }
      isPending={exportMutation.isPending}
      className={className}
    />
  )
}
